import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormattingService } from '@app/shared/services/formatting.service';
import { FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { BPProfilesAccessSettingsDataService } from './bp-profiles-access-settings-data.service';
import { BPProfilesService } from '../../bp-profiles.service';
import { UserDataService } from '@app/shared/user/user-data.service';
import _ from 'underscore';
import { util } from '@app/shared/util';
import { BaseBPProfilesDetailComponent } from '../base-bp-profiles-detail.component';
import { Override } from '@app/shared/app-decorators';
import { IBPProfile, IGenericUser } from '@type_definitions/datatypes';
import { BPProfilesDataService } from '@app/main/bp-profiles/bp-profiles-data.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { EMAIL_SUFFIX_REGEX } from '@app/shared/formly/ga-formly-validator.service';
import { ContactInfoComponent } from '@app/shared/contact/contact-info.component';
import { IRole } from '@app/main/bp-profiles/advanced/contacts/bp-profiles-contacts-data.service';

import { RoleContactType } from '@app/shared/contact/role-contact-type';

interface IBPProfileAccessSettingsModel {
  newUserApprovalNeeded?:boolean;
  genericUser?:IGenericUser;
  emailSuffix?:string;
  userAdministrator?:IRole;
  companyRegistrationUrl?:string;
}

@Component({
  selector: 'bp-profiles-access-settings',
  template: `
    <h2 *ngIf="loading"><activity-indicator></activity-indicator></h2>
    
    <div *ngIf="!loading" class="bp-profiles-access-settings">
      <div *ngIf="model.newUserApprovalNeeded && noUserAdministrator" class="alert alert-warning">
        <i class="fa fa-warning"></i>
        <span class="col-margin-left margin-right-xs" translate>BPPROFILES.ACCESS_SETTINGS.ADMINISTRATOR_NOT_SET</span> 
        <a [href]="'#/bpprofiles/' + profileId + '/contacts'" translate>BPPROFILES.ACCESS_SETTINGS.SET_ADMINISTRATOR</a>
      </div>
      
      <formly [form]="form" [fields]="fields" [model]="model"></formly>
      
      <faq-item faqCode="BP_USER_REGISTRATION"></faq-item>
    </div>
  `,
  styleUrls: ['./bp-profiles-access-settings.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BPProfilesAccessSettingsComponent extends BaseBPProfilesDetailComponent implements OnInit {
  public model:IBPProfileAccessSettingsModel = {
    newUserApprovalNeeded: false
  };
  public profileId:number;
  public newUserApprovalNeeded:boolean;
  public noUserAdministrator:boolean;

  private userAdministrator:IRole;
  private genericUserField:IExtraFieldConfigurationObject = {};
  private companyRegistrationUrlField:IExtraFieldConfigurationObject = {};
  private userAdministratorField:IExtraFieldConfigurationObject = {};

  constructor(
    private formattingService:FormattingService,
    private bpProfilesAccessSettingsDataService:BPProfilesAccessSettingsDataService,
    private formlyService:GaFormlyService,
    private userDataService:UserDataService,
    private bpProfilesDataService:BPProfilesDataService,
    private translationService:TranslationService,
    protected bpProfilesService:BPProfilesService
  ) {
    super(bpProfilesService);
  }

  @Override(BaseBPProfilesDetailComponent)
  public ngOnInit():void {
    this.profileId = this.bpProfilesService.activeProfile?.id;
    super.ngOnInit();
  }

  @Override(BaseBPProfilesDetailComponent)
  public onProfileDetailLoaded(profileDetail:IBPProfile):void {
    super.onProfileDetailLoaded(profileDetail);

    // init model
    this.model = {
      newUserApprovalNeeded: profileDetail.newUserApprovalNeeded,
      genericUser: profileDetail.genericUser,
      emailSuffix: profileDetail.emailSuffix
    };

    this.companyRegistrationUrlField.templateOptions.loading = true;
    this.userDataService.createSignupLink(this.bpProfilesService.activeProfile?.company?.id)
      .then((link:string) => {
        this.companyRegistrationUrlField.formControl.setValue(link);
      })
      .finally(() => {
        this.companyRegistrationUrlField.templateOptions.loading = false;
      });

    this.userAdministratorField.templateOptions.loading = true;
    this.bpProfilesAccessSettingsDataService.getCompanyRoles(this.profileId, this.bpProfilesService.activeProfile?.company?.id)
      .then((roles:IRole[]) => {
        this.userAdministrator = _.find(roles, (role:IRole) => util.getValue(role, 'contactType.code') === RoleContactType.USER_ADMINISTRATOR);
        this.userAdministratorField.formControl.setValue(this.userAdministrator);
        this.userAdministratorField.templateOptions.inputs.roleContact = this.userAdministrator;
        this.noUserAdministrator = _.isUndefined(this.userAdministrator?.contact);
      })
      .finally(() => {
        this.userAdministratorField.templateOptions.loading = false;
      });
  }

  protected fillFields():void {
    this.formlyService.fillField(this.genericUserField, this.bpProfilesAccessSettingsDataService.getGenericContacts(this.profileId));
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-sm-3';
    const valueClass:string = 'col-sm-9';
    const editingMode:boolean = this.bpProfilesService.editingMode;

    return [
      this.formlyService.aField(editingMode? FormlyType.CHECKBOX : FormlyType.VALUE, 'newUserApprovalNeeded')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatBoolean)
        .withoutInfoOffset()
        .showInfo()
        .withExtraOptions({
          templateOptions: {
            inline: true,
            noWrapper: editingMode
          }
        })
        .build(),
      this.genericUserField = this.formlyService.aField(editingMode? FormlyType.SELECT : FormlyType.VALUE, 'genericUser')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatLov)
        .withoutInfoOffset()
        .showInfo()
        .build(),
      this.formlyService.aField(editingMode? FormlyType.INPUT : FormlyType.VALUE, 'emailSuffix')
        .withInfo('GA.GENERAL.EMAILSUFFIX_INFO')
        .withPattern(EMAIL_SUFFIX_REGEX)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withoutInfoOffset()
        .showInfo()
        .build(),
      this.userAdministratorField = this.formlyService.aCustom('BPPROFILES.ACCESS_SETTINGS.USERADMINISTRATOR.LABEL')
        .withComponent(ContactInfoComponent)
        .withLabelClass(labelClass)
        .withValueClass(`${valueClass} user-administrator-value`)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              roleContact: this.userAdministrator || this.model.userAdministrator
            }
          }
        })
        .build(),
      this.companyRegistrationUrlField = this.formlyService.aValue('companyRegistrationUrl')
        .withLabelClass(labelClass)
        .withValueClass(`${valueClass} company-registration-url-value`)
        .withClipboard()
        .build()
    ];
  }

  protected onSave = ():void => {
    const data:IBPProfile = util.copyAndExtend(this.bpProfilesService.profileDetail, {
      newUserApprovalNeeded: this.model.newUserApprovalNeeded,
      genericUser: this.model.genericUser,
      emailSuffix: this.model.emailSuffix
    });

    this.setSaveBusy(true);
    this.bpProfilesDataService.updateBPProfileDetail(this.bpProfilesService.activeProfile?.id, data, this.translationService.instant('BPPROFILES.ACCESS_SETTINGS.TEXT'))
      .then((profileDetail:IBPProfile) => {
        this.bpProfilesService.profileDetail = profileDetail;
      })
      .catch(util.noop)
      .finally(() => {
        this.setSaveBusy(false);
      });
  };
}